import React from "react";
import styled from "styled-components";

const PageWrapper = styled.div`
  padding-top: 120px;
  min-height: 100vh;
  background: #fff;
`;

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 20px;
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 60px;
`;

const PageTitle = styled.h1`
  font-size: 3rem;
  color: var(--text-dark);
  margin-bottom: 20px;
  font-family: "Phudu", sans-serif;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const LastUpdated = styled.p`
  color: var(--text-light);
  font-style: italic;
`;

const Content = styled.div`
  h2 {
    color: var(--text-dark);
    margin: 40px 0 20px 0;
    font-family: "Phudu", sans-serif;
  }

  h3 {
    color: var(--text-dark);
    margin: 30px 0 15px 0;
    font-family: "Phudu", sans-serif;
  }

  p {
    color: var(--text-light);
    line-height: 1.7;
    margin-bottom: 20px;
  }

  ul,
  ol {
    color: var(--text-light);
    line-height: 1.7;
    margin-bottom: 20px;
    padding-left: 20px;
  }

  li {
    margin-bottom: 8px;
  }

  strong {
    color: var(--text-dark);
  }
`;

const ContactInfo = styled.div`
  background: #f8f9fa;
  padding: 30px;
  border-radius: 15px;
  margin-top: 40px;
  text-align: center;

  h3 {
    color: var(--text-dark);
    margin-bottom: 20px;
    font-family: "Phudu", sans-serif;
  }

  p {
    color: var(--text-light);
    margin-bottom: 10px;
  }

  a {
    color: var(--secondary-color);
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const PrivacyPolicy = () => {
  return (
    <PageWrapper>
      <Container>
        <PageHeader>
          <PageTitle>Privacy Policy</PageTitle>
          <LastUpdated>Last updated: October 14, 2025</LastUpdated>
        </PageHeader>

        <Content>
          <h2>1. Information We Collect</h2>
          <p>
            At Baypoint Storage, we collect information you provide directly to
            us, such as when you:
          </p>
          <ul>
            <li>Request a quote or contact us for information</li>
            <li>Purchase or rent containers from us</li>
            <li>Subscribe to our newsletter or marketing communications</li>
            <li>Visit our website and interact with our services</li>
          </ul>

          <h3>Personal Information</h3>
          <p>
            This may include your name, email address, phone number, mailing
            address, billing information, and any other information you choose
            to provide.
          </p>

          <h3>Usage Information</h3>
          <p>
            We automatically collect certain information when you visit our
            website, including your IP address, browser type, pages visited, and
            interaction data.
          </p>

          <h2>2. How We Use Your Information</h2>
          <p>We use the information we collect to:</p>
          <ul>
            <li>Provide, maintain, and improve our services</li>
            <li>Process transactions and send related information</li>
            <li>Send you technical notices, updates, and support messages</li>
            <li>
              Respond to your comments, questions, and customer service requests
            </li>
            <li>
              Communicate with you about products, services, offers, and events
            </li>
            <li>Monitor and analyze trends, usage, and activities</li>
          </ul>

          <h2>3. Information Sharing and Disclosure</h2>
          <p>
            We do not sell, trade, or otherwise transfer your personal
            information to third parties except in the following circumstances:
          </p>
          <ul>
            <li>
              <strong>Service Providers:</strong> We may share information with
              contractors, service providers, and other third parties who
              perform services on our behalf
            </li>
            <li>
              <strong>Legal Requirements:</strong> We may disclose information
              if required to do so by law or in response to valid requests by
              public authorities
            </li>
            <li>
              <strong>Business Transfers:</strong> Information may be
              transferred in connection with a merger, acquisition, or sale of
              assets
            </li>
            <li>
              <strong>Consent:</strong> We may share information with your
              consent or at your direction
            </li>
          </ul>

          <h2>4. Data Security</h2>
          <p>
            We implement appropriate security measures to protect your personal
            information against unauthorized access, alteration, disclosure, or
            destruction. However, no method of transmission over the internet is
            100% secure.
          </p>

          <h2>5. Your Rights and Choices</h2>
          <p>
            You have the following rights regarding your personal information:
          </p>
          <ul>
            <li>
              <strong>Access:</strong> You can request access to your personal
              information
            </li>
            <li>
              <strong>Correction:</strong> You can request correction of
              inaccurate information
            </li>
            <li>
              <strong>Deletion:</strong> You can request deletion of your
              personal information
            </li>
            <li>
              <strong>Opt-out:</strong> You can opt-out of marketing
              communications at any time
            </li>
          </ul>

          <h2>6. Cookies and Tracking Technologies</h2>
          <p>
            We use cookies and similar technologies to enhance your experience
            on our website. You can control cookie preferences through your
            browser settings.
          </p>

          <h2>7. Third-Party Links</h2>
          <p>
            Our website may contain links to third-party websites. We are not
            responsible for the privacy practices or content of these external
            sites.
          </p>

          <h2>8. Children's Privacy</h2>
          <p>
            Our services are not intended for children under 13 years of age. We
            do not knowingly collect personal information from children under
            13.
          </p>

          <h2>9. Changes to This Privacy Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will notify
            you of any changes by posting the new Privacy Policy on this page
            and updating the "Last updated" date.
          </p>

          <ContactInfo>
            <h3>Contact Us</h3>
            <p>
              If you have any questions about this Privacy Policy, please
              contact us:
            </p>
            <p>
              <strong>Email:</strong>{" "}
              <a href="mailto:privacy@baypointstorage.com">
                privacy@baypointstorage.com
              </a>
            </p>
            <p>
              <strong>Phone:</strong>{" "}
              <a href="tel:+14845429847">(484) 542-9847</a>
            </p>
            <p>
              <strong>Address:</strong> Baypoint Storage, Portage, Indiana,
              United States
            </p>
          </ContactInfo>
        </Content>
      </Container>
    </PageWrapper>
  );
};

export default PrivacyPolicy;
